import type { MethodMixTotal } from '../../types/dashboard';

/** Human-readable labels and colors for booking methods. */
const METHOD_CONFIG: Record<string, { label: string; color: string }> = {
  'in-person': { label: 'In-person', color: '#6366f1' },  // indigo
  video:       { label: 'Video',     color: '#0ea5e9' },  // sky
  phone:       { label: 'Phone',     color: '#8b5cf6' },  // violet
  unknown:     { label: 'Other',     color: '#94a3b8' },  // slate
};

function methodLabel(method: string): string {
  return METHOD_CONFIG[method]?.label ?? method;
}

function methodColor(method: string): string {
  return METHOD_CONFIG[method]?.color ?? '#94a3b8';
}

interface MethodMixProps {
  totals: MethodMixTotal[];
  total: number;
  loading?: boolean;
  error?: boolean;
  forbidden?: boolean;
  periodLabel?: string;
  onRetry?: () => void;
}

export function MethodMix({
  totals,
  total,
  loading = false,
  error = false,
  forbidden = false,
  periodLabel,
  onRetry,
}: MethodMixProps) {

  // Forbidden state
  if (forbidden) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Booking methods</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex items-center justify-center min-h-[120px]">
          <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Booking methods</h3>
        </div>
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] text-ink-4 mb-3">Couldn't load method data.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-[11.5px] font-medium text-tide hover:text-tide/80"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading && totals.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-32 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-4 pb-3 space-y-3">
          <div className="h-6 w-full bg-canvas animate-pulse rounded" />
          <div className="flex gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-3 w-20 bg-canvas animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (total === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="flex items-center gap-2">
            <h3 className="text-[14px] font-semibold">Booking methods</h3>
            {periodLabel && (
              <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
            )}
          </div>
        </div>
        <div className="px-4 pt-4 pb-3 flex flex-col items-center justify-center min-h-[120px] gap-2">
          <p className="text-[11.5px] text-ink-4">
            No bookings in this period to analyze.
          </p>
        </div>
      </div>
    );
  }

  // Sort by count descending for the breakdown list
  const sorted = [...totals].sort((a, b) => b.count - a.count);

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b">
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-semibold">Booking methods</h3>
          {periodLabel && (
            <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
          )}
        </div>
      </div>

      <div className="px-4 pt-4 pb-3">
        {/* Stacked bar */}
        <div className="flex h-6 rounded overflow-hidden mb-4">
          {sorted.map((item) => {
            const pct = (item.count / total) * 100;
            if (pct === 0) return null;
            return (
              <div
                key={item.method}
                className="h-full transition-all duration-300"
                style={{
                  width: `${pct}%`,
                  backgroundColor: methodColor(item.method),
                }}
                title={`${methodLabel(item.method)}: ${item.count} (${pct.toFixed(0)}%)`}
              />
            );
          })}
        </div>

        {/* Legend + counts */}
        <div className="space-y-2">
          {sorted.map((item) => {
            const pct = (item.count / total) * 100;
            return (
              <div key={item.method} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ backgroundColor: methodColor(item.method) }}
                  />
                  <span className="text-[12px] text-ink-2">
                    {methodLabel(item.method)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-medium num">
                    {item.count}
                  </span>
                  <span className="text-[10.5px] text-ink-4 num w-10 text-right">
                    {pct.toFixed(0)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
